import { Dropdown, IDropdownOption, IDropdownStyleProps, IDropdownStyles, IStyleFunctionOrObject } from '@fluentui/react';
import React from 'react';
import { activeFontPackFonts, FONT_SLOT_LABELS, FONT_SLOTS, FONT_TOKEN_PREFIX, getActiveFontPack } from '../Storefront/Editor/stylesConfig';
import { useStorefrontStyles } from '../Storefront/Editor/StorefrontStylesContext';

interface IFontPickerProps {
    selectedFont?: string;
    onChange: (font: string) => void;
    label?: string;
    placeholder?: string;
    // Passed straight to the underlying Fluent Dropdown so callers can size/skin it (e.g. the
    // Storefront Styles panel, which needs every control in a row to fill one fixed cell).
    styles?: IStyleFunctionOrObject<IDropdownStyleProps, IDropdownStyles>;
    /**
     * Offer the active Font Pack's slots as choices, stored as a `$font:heading` / `$font:body`
     * TOKEN rather than the literal stack - so the value keeps following the pack.
     *
     * OPT-IN for the same reason the colour picker's palette strip is (see IStyleEntry there):
     * a token is only safe where the value passes through the token resolvers on its way to the
     * page. Inert when no font pack is active.
     */
    allowPaletteTokens?: boolean;
}

// Curated stacks only, and every entry has to render on a shopper's machine: either from a font
// they already have, or from one the storefront app itself loads. The option KEY is the CSS stack
// that gets stored; keys for the three original entries are unchanged so existing storefront
// settings keep resolving.
//
// MIRROR: api/.../MachineLearning/Assistant/Validation/StorefrontFontStacks.cs holds the same list
// so the AI assistant offers and accepts exactly what this picker does. Change one, change both.
export const FONT_STACKS: { name: string; stack: string }[] = [
    { name: 'System UI', stack: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
    { name: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
    { name: 'Segoe UI', stack: 'Segoe UI, Tahoma, Geneva, sans-serif' },
    { name: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
    { name: 'Tahoma', stack: 'Tahoma, Geneva, sans-serif' },
    { name: 'Trebuchet MS', stack: 'Trebuchet MS, Helvetica, sans-serif' },
    { name: 'Gill Sans', stack: 'Gill Sans, Gill Sans MT, Calibri, sans-serif' },

    // The five families the storefront loads for the shopper. Each keeps a system fallback behind it
    // so a blocked font request degrades to something of the same shape rather than to Times.
    { name: 'Poppins', stack: 'Poppins, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
    { name: 'Inter', stack: 'Inter, system-ui, "Segoe UI", sans-serif' },
    { name: 'Open Sans', stack: 'Open Sans, "Segoe UI", Roboto, sans-serif' },
    { name: 'Roboto', stack: 'Roboto, "Segoe UI", Arial, sans-serif' },
    { name: 'Syne', stack: 'Syne, "Segoe UI", sans-serif' },

    { name: 'Georgia', stack: 'Georgia, serif' },
    { name: 'Palatino', stack: 'Palatino Linotype, Book Antiqua, Palatino, serif' },
    { name: 'Times New Roman', stack: 'Times New Roman, Times, serif' },
    { name: 'Garamond', stack: 'Garamond, Baskerville, Baskerville Old Face, serif' },
    { name: 'Courier New', stack: 'Courier New, Courier, monospace' },
    { name: 'Monaco', stack: 'Monaco, Consolas, monospace' },
];

const FontPicker: React.FC<IFontPickerProps> = (props) => {
    const ctx = useStorefrontStyles();
    // The active font pack's two slots, offered above the literal stacks so they read as the
    // preferred choice. Empty (and therefore invisible) with no pack active or no opt-in.
    const fontPack = props.allowPaletteTokens ? getActiveFontPack(ctx.styles) : undefined;
    const packFonts = fontPack ? activeFontPackFonts(ctx.styles) : undefined;
    const tokenOptions: IDropdownOption[] = packFonts
        ? FONT_SLOTS
            .filter((slot) => typeof packFonts[slot] === 'string' && !!packFonts[slot])
            .map((slot) => ({
                key: `${FONT_TOKEN_PREFIX}${slot}`,
                // "(palette)" rather than the pack's name: the two token families read as one
                // "follows the brand" concept, and the pack can be renamed without stale labels.
                text: `${FONT_SLOT_LABELS[slot]} (palette)`,
                data: packFonts[slot] as string,
            }))
        : [];

    const dropdownOptions: IDropdownOption[] = [
        { key: '', text: 'Default' },
        ...tokenOptions,
        ...FONT_STACKS.map((f) => ({ key: f.stack, text: f.name, data: f.stack })),
    ];

    // Legacy stored values that are no longer literal option keys (or hand-typed stacks) still need
    // to display: surface them as an extra option rather than showing an empty control.
    if (props.selectedFont && !dropdownOptions.some((o) => o.key === props.selectedFont)) {
        dropdownOptions.push({ key: props.selectedFont, text: props.selectedFont, data: props.selectedFont });
    }

    return (
        <Dropdown
            label={props.label}
            selectedKey={props.selectedFont}
            placeholder={props.placeholder}
            onChange={(e, option) => {
                if (option !== undefined) {
                    props.onChange(option.key as string);
                }
            }}
            onRenderOption={(option) => (
                <span style={option?.data ? { fontFamily: option.data as string } : undefined}>
                    {option?.text}
                </span>
            )}
            options={dropdownOptions}
            styles={props.styles}
        />
    );
};

export default FontPicker;
